import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';
import { Link2, Users } from 'lucide-react';
import { InfoButton } from '../../components/InfoModal';
import LinkExpertModal from './LinkExpertModal';

const ORG_TYPE_LABEL = {
  AGENT: 'Our agent company',
  PARTNER: 'Partner (Form 12A)',
  IQP_COMPANY: 'IQP company (Council register)',
  ENGINEERING_FIRM: 'Engineering firm',
};

function ssSummary(org) {
  const entries = Object.entries(org.iqps_by_ss || {}).filter(([, list]) => list.length > 0);
  if (!entries.length) return { codes: [], people: 0 };
  const codes = entries.map(([code]) => code);
  const people = entries.reduce((n, [, list]) => n + list.length, 0);
  return { codes, people };
}

function naturalNameCompare(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
}

export default function AgentTab() {
  const navigate = useNavigate();
  const [linkOrg, setLinkOrg] = useState(null);

  const { data: overview, isLoading } = useQuery({
    queryKey: ['service-network', 'overview'],
    queryFn: async () => (await api.get('/service-network/overview')).data?.data ?? {},
  });

  const organizations = overview?.organizations ?? [];
  const companyOrgs = [...organizations.filter((o) => o.org_type !== 'AGENT')].sort((a, b) =>
    naturalNameCompare(a.name, b.name)
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-gray-900">Partner companies</h2>
        <InfoButton title="Partner companies" label="Info">
          <div className="space-y-2 text-sm">
            <p>
              Supplier / partner companies that inspect SS on our portfolio buildings. Open a company, then use{' '}
              <strong>Register our partner</strong> to copy that company&apos;s details into the Our Partner
              registration form.
            </p>
            <p>
              <strong>List below:</strong> Open a row for company details, buildings &amp; SS history. Use Link
              engineer to attach amendment experts.
            </p>
          </div>
        </InfoButton>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-5 h-5 text-navy-800" />
          <h2 className="text-lg font-semibold text-navy-900">Companies & IQPs ({companyOrgs.length})</h2>
        </div>
        {isLoading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : companyOrgs.length === 0 ? (
          <p className="text-sm text-gray-500 p-4 bg-card rounded-lg border">
            No companies yet. Open Registered IQP to import, or register under Our Partner.
          </p>
        ) : (
          <div className="bg-card rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-navy-900 text-left text-xs uppercase tracking-wide text-white/80 border-b border-navy-950">
                  <tr>
                    <th className="px-4 py-3 w-12">#</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">SS / IQP</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {companyOrgs.map((org, idx) => {
                    const { codes, people } = ssSummary(org);
                    return (
                      <tr
                        key={org.id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/network/org/${org.id}`)}
                      >
                        <td className="px-4 py-3 text-gray-400 tabular-nums">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{org.name}</div>
                          {org.license_number ? (
                            <div className="text-xs text-gray-500">Licence {org.license_number}</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {ORG_TYPE_LABEL[org.org_type] || org.org_type}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {[org.email, org.phone].filter(Boolean).join(' · ') || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <span className="font-medium">
                            {org.iqp_count ?? people} IQP
                            {org.expert_count ? ` · ${org.expert_count} eng` : ''}
                          </span>
                          {codes.length > 0 ? (
                            <div className="text-xs text-navy-800 mt-0.5 truncate max-w-xs">
                              {codes.join(', ')}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 mt-0.5">No SS linked yet</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {org.org_type !== 'AGENT' && (
                            <button
                              type="button"
                              onClick={() => setLinkOrg(org)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-navy-200 text-navy-900 hover:bg-navy-50 mr-2"
                            >
                              <Link2 className="w-3.5 h-3.5" />
                              Link engineer
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/network/org/${org.id}?edit=1`)}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs rounded-lg border border-primary-300 text-primary-800 hover:bg-primary-50 mr-2"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/network/org/${org.id}`)}
                            className="inline-flex items-center px-2.5 py-1.5 text-xs rounded-lg bg-navy-900 text-white hover:bg-navy-800"
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {linkOrg && <LinkExpertModal organization={linkOrg} onClose={() => setLinkOrg(null)} />}
    </div>
  );
}
